import { ControlList, useDisplayAlert, useFormData, useTranslations, useUrlParamsValue, useUrlParamsValueToString } from "@harea/component";
import { useNavigate, useParams } from "react-router-dom";
import { useTimeOffControlForm } from "../hooks/useTimeOffControlForm";
import { useTimeOffInvalidateQuery } from "hooks/useTimeOffInvalidateQuery";
import { useProjectTimeOffNavigateBack } from "views/create/hooks/useProjectTimeOffNavigateBack";
import { useCountSoldMutation } from "hooks/useCountSoldMutation";
import { useBulkTimeOffControlForm } from "../hooks/useBulkTimeOffControlForm";
import { useValidateBulkTimeoffCreationMutation } from "../hooks/useValidateBulkTimeoffCreationMutation";
import { useValidateDayOffMutation } from "hooks/useValidateDayOffMutation";
import { InsufficientQuotaDialog, InsufficientQuotaError } from "components/InsufficientQuotaDialog";
import { useState } from "react";
import { useQueryClient } from "react-query";

const INSUFFICIENT_QUOTA_ERROR = "INSUFFICIENT_TIMEOFF_QUOTA";
const INSUFFICIENT_LEAVE_BALANCE_ERROR = "INSUFFICIENT_LEAVE_BALANCE";

const ProjectTimeOffCreateLeavingContainer = () => {

    const { id } = useParams();

    const { formData } = useFormData<any>({
        id: "timeoff-add-leaving-form",
        formData: {}
    });

    const typeValue = formData?.type;
    const typeDesignation = typeof typeValue === 'object' ? typeValue?.designation ?? typeValue?.value : typeValue;
    const isAbsenceType = typeDesignation === 'ABSENCE';

    const urlParams = useUrlParamsValue()
    const urlParamsToString = useUrlParamsValueToString()
    const bulkControl = useBulkTimeOffControlForm()
    const control = useTimeOffControlForm(id ? parseInt(id) : undefined, formData?.type);
    const invalidateTimeOffsQuery = useTimeOffInvalidateQuery()
    const navigateBack = useProjectTimeOffNavigateBack()
    const navigate = useNavigate()
    const countSoldMutation = useCountSoldMutation()
    const validateBulkTimeoffCreationMutation = useValidateBulkTimeoffCreationMutation()
    const validateDayOffMutation = useValidateDayOffMutation()
    const queryClient = useQueryClient()
    const addAlert = useDisplayAlert()

    const [VALIDATE_ERROR] = useTranslations([
        "Harea.Project.DayOff.Validate.Error.Message",
    ])

    const [insufficientQuotaError, setInsufficientQuotaError] = useState<InsufficientQuotaError | null>(null)
    const [insufficientQuotaIds, setInsufficientQuotaIds] = useState<number[]>([])
    const [isValidatingAlternative, setIsValidatingAlternative] = useState(false)

    const refreshBalances = () => {
        invalidateTimeOffsQuery()
        queryClient.invalidateQueries('App.bo.ContractManagerQuery')
    }

    const handleQuotaError = (error: any, ids: number[]) => {
        const response = error?.response
        const type = typeof formData?.type === 'object' ? formData?.type?.designation ?? formData?.type?.value : formData?.type
        if (type === 'ABSENCE') return false
        if (response?.error === INSUFFICIENT_QUOTA_ERROR || response?.error === INSUFFICIENT_LEAVE_BALANCE_ERROR) {
            setInsufficientQuotaError(response)
            setInsufficientQuotaIds(ids)
            return true
        }
        return false
    }

    const handleSubmited = (val : any) => {

        if(formData?.type === 'ABSENCE') {
            countSoldMutation.mutate(val?.id)
        }

        if (urlParams?.bulk) {
            const ids = (val?.ids ?? []).map(Number)
            validateBulkTimeoffCreationMutation.mutate({ ids }, {
                onSuccess: () => {
                    refreshBalances()
                    navigateBack()
                },
                onError: (error) => {
                    if (formData?.type !== 'ABSENCE' && handleQuotaError(error, ids)) return
                    addAlert({ text: VALIDATE_ERROR, type: 'danger', dismissTimeout: 10000 })
                    refreshBalances()
                    navigateBack()
                },
            })
            return
        }

        refreshBalances()
        navigateBack();
    };

    const handleQuotaAlternativeConfirm = async (alternative: string) => {
        if (!insufficientQuotaIds.length) return
        setIsValidatingAlternative(true)
        try {
            await Promise.all(
                insufficientQuotaIds.map((timeoffId) =>
                    validateDayOffMutation.mutateAsync({ id: timeoffId, data: { [alternative]: true } })
                )
            )
            setIsValidatingAlternative(false)
            setInsufficientQuotaError(null)
            setInsufficientQuotaIds([])
            refreshBalances()
            navigateBack()
        } catch (error : any) {
            setIsValidatingAlternative(false)
            const response = error?.response
            if (response?.error === INSUFFICIENT_QUOTA_ERROR || response?.error === INSUFFICIENT_LEAVE_BALANCE_ERROR) {
                setInsufficientQuotaError(response)
                return
            }
            setInsufficientQuotaError(null)
            setInsufficientQuotaIds([])
            addAlert({ text: VALIDATE_ERROR, type: 'danger', dismissTimeout: 10000 })
            navigateBack()
        }
    }

    const handleQuotaDialogClose = () => {
        if (isValidatingAlternative) return
        setInsufficientQuotaError(null)
        setInsufficientQuotaIds([])
        navigateBack()
    }

    return (
        <>
            <ControlList
                {...(urlParams.bulk ? bulkControl : control)}
                defaultValue={{
                    dateBegin: urlParams?.eventDate,
                    employee: urlParams?.employeeFilterCalendar && !urlParams?.bulk ? { id: urlParams?.employeeFilterCalendar } : undefined
                }}
                id="timeoff-add-leaving-form"
                onSubmited={handleSubmited}
                onCancel={() => {
                    const params = new URLSearchParams(urlParamsToString);
                    if (params.has('bulk')) {
                        params.delete('bulk');
                    }
                    navigate(-1)
                }}
            />
            <InsufficientQuotaDialog
                open={!!insufficientQuotaError && !isAbsenceType}
                error={insufficientQuotaError}
                isLoading={isValidatingAlternative}
                onClose={handleQuotaDialogClose}
                onConfirm={handleQuotaAlternativeConfirm}
            />
        </>
    );
};

export default ProjectTimeOffCreateLeavingContainer;